import React from 'react'
import { createRootRoute, createRoute, Outlet, useParams } from '@tanstack/react-router'

import { AuthLayout } from '@shared/components/auth/auth-layout'
import { MainLayout } from '@/shared/components/layout/main-layout'
import { SecureRoute } from '@shared/components/auth/secure-route'
import { USER_PERMISSIONS } from '@shared/constants/user-constants'
import { SmartRedirect } from '@shared/components/auth/smart-redirect'
import { RedirectIfAuthenticated } from '@shared/components/auth/redirect-authenticated-route'
import { billingSearchSchema } from '@admin/features/billing/billing-search-schema'
import { transactionsSearchSchema } from '@admin/features/transactions/transactions-search-schema'
import { usersSearchSchema } from '@admin/features/users/users-search-schema'
import { f1SettingsSearchSchema } from '@admin/features/f1-settings/f1-settings-search-schema'
import { sanitizeSearchParams } from '@shared/lib/url-search-utils'
import { Toaster } from '@/shared/components/ui/sonner'

import { createFingerprintingRoutes } from './domains/fingerprinting/routes'
import { createImmigrationRoutes } from './domains/immigration/routes'

// Lazy-loaded route components (code-splitting)
const LazyLoginForm = React.lazy(() =>
  import('@shared/components/auth/login-form').then((m) => ({ default: m.LoginForm }))
)
const LazyRegisterForm = React.lazy(() =>
  import('@shared/components/auth/register-form').then((m) => ({ default: m.RegisterForm }))
)
const LazyForgotPasswordForm = React.lazy(() =>
  import('@shared/components/auth/forgot-password-form').then((m) => ({ default: m.ForgotPasswordForm }))
)
const LazyResetPasswordForm = React.lazy(() =>
  import('@shared/components/auth/reset-password-form').then((m) => ({ default: m.ResetPasswordForm }))
)
const LazyContactSupport = React.lazy(() =>
  import('@shared/components/auth/contact-support').then((m) => ({ default: m.ContactSupport }))
)
const LazyFingerprintingPublicForm = React.lazy(() => import('@/public/fingerprinting-form'))
const LazyTransactionsDashboard = React.lazy(() =>
  import('@admin/features/transactions/components').then((m) => ({ default: m.TransactionsDashboard }))
)
const LazyUsersList = React.lazy(() =>
  import('@admin/features/users/components').then((m) => ({ default: m.UsersList }))
)
const LazyBilling = React.lazy(() =>
  import('@admin/features/billing/billing').then((m) => ({ default: m.Billing }))
)
const LazyF1Settings = React.lazy(() =>
  import('@admin/features/f1-settings/f1-settings').then((m) => ({ default: m.F1Settings }))
)
const LazyForbiddenPage = React.lazy(() =>
  import('@shared/components/error/forbidden-page').then((m) => ({ default: m.ForbiddenPage }))
)

const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth',
  component: AuthLayout,
})

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'login',
  component: () => (
    <RedirectIfAuthenticated>
      <LazyLoginForm />
    </RedirectIfAuthenticated>
  ),
})

const registerRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'register',
  component: () => (
    <RedirectIfAuthenticated>
      <LazyRegisterForm />
    </RedirectIfAuthenticated>
  ),
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'forgot-password',
  component: () => <LazyForgotPasswordForm />,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'reset-password/:token',
  component: function ResetPasswordWrapper() {
    const params = useParams({ strict: false }) as { token?: string }
    return <LazyResetPasswordForm token={params.token ?? ''} />
  },
})

const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'support',
  component: () => <LazyContactSupport />,
})

const publicFingerprintingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'fingerprinting-request',
  component: () => (
    <>
      <Toaster />
      <LazyFingerprintingPublicForm />
    </>
  ),
})

// Protected app routes (legacy - will redirect to /admin)
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: () => (
    <SecureRoute>
      <MainLayout />
    </SecureRoute>
  ),
})

const rootIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: () => (
    <SecureRoute>
      <SmartRedirect />
    </SecureRoute>
  ),
})

// Admin routes with /admin namespace
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'admin',
  component: () => (
    <SecureRoute>
      <MainLayout />
    </SecureRoute>
  ),
})

const adminTransactionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'transactions',
  validateSearch: (search: Record<string, unknown>) => {
    const sanitized = sanitizeSearchParams(search)
    
    if (sanitized.selectedLocations) {
      if (Array.isArray(sanitized.selectedLocations)) {
        sanitized.selectedLocations = sanitized.selectedLocations.join(',')
      } else if (typeof sanitized.selectedLocations === 'string') {
        try {
          const parsed = JSON.parse(sanitized.selectedLocations)
          if (Array.isArray(parsed)) {
            sanitized.selectedLocations = parsed.join(',')
          }
        } catch {
          // Already a comma-separated string, keep as is
        }
      }
    }
    
    const result = transactionsSearchSchema.safeParse(sanitized)
    if (result.success) {
      const validated = { ...result.data }
      if (validated.page === 1) {
        delete (validated as { page?: number }).page
      }
      return validated
    }
    return {}
  },
  component: () => (
    <SecureRoute requiredPermissions={[USER_PERMISSIONS.VIEW_TRANSACTIONS]}>
      <LazyTransactionsDashboard />
    </SecureRoute>
  ),
})


const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'users',
  validateSearch: (search: Record<string, unknown>) => {
    if (typeof search.page === 'string') {
      const trimmed = search.page.replace(/^"+|"+$/g, '')
      const parsed = Number(trimmed)
      if (!isNaN(parsed)) {
        search.page = parsed
      } else {
        delete (search as { page?: unknown }).page
      }
    }

    const sanitized = sanitizeSearchParams(search)
    const result = usersSearchSchema.safeParse(sanitized)
    if (result.success) {
      const validated = { ...result.data }
      const safePage = Number.isFinite(validated.page) && validated.page ? validated.page : 1

      if (safePage === 1) {
        delete (validated as { page?: number }).page
      } else {
        (validated as { page?: number }).page = safePage
      }
      return validated
    }
    return {}
  },
  component: () => (
    <SecureRoute requiredPermissions={[USER_PERMISSIONS.VIEW_USER]}>
      <LazyUsersList />
    </SecureRoute>
  ),
})

const adminBillingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'billing',
  validateSearch: (search: Record<string, unknown>) => {
    const sanitized = sanitizeSearchParams(search)
    const result = billingSearchSchema.safeParse(sanitized)
    if (result.success) {
      const validated = { ...result.data }
      if (validated.page === 1) {
        delete (validated as { page?: number }).page
      }
      return validated
    }
    return {}
  },
  component: () => (
    <SecureRoute requiredPermissions={[USER_PERMISSIONS.VIEW_REPORTS]}>
      <LazyBilling />
    </SecureRoute>
  ),
})

const adminF1SettingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'f1-settings',
  validateSearch: (search: Record<string, unknown>) => {
    const sanitized = sanitizeSearchParams(search)
    const result = f1SettingsSearchSchema.safeParse(sanitized)
    if (result.success) {
      return result.data
    }
    return {}
  },
  component: () => (
    <SecureRoute requiredPermissions={[USER_PERMISSIONS.VIEW_ORGANIZATION]}>
      <LazyF1Settings />
    </SecureRoute>
  ),
})

// 404 Not Found route
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: () => <div>404 - Page Not Found</div>,
})

// 403 Forbidden route (global)
const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'forbidden',
  component: () => <LazyForbiddenPage />,
})

// Create domain routes using factory functions
const { routeTree: fingerprintingRouteTree } = createFingerprintingRoutes(rootRoute)
const { routeTree: immigrationRouteTree } = createImmigrationRoutes(rootRoute)

// Create the route tree
export const routeTree = rootRoute.addChildren([
  authRoute.addChildren([loginRoute, registerRoute, forgotPasswordRoute, resetPasswordRoute]),
  supportRoute,
  publicFingerprintingRoute,
  appRoute.addChildren([
    rootIndexRoute,
  ]),
  adminRoute.addChildren([
    adminTransactionsRoute,
    adminUsersRoute,
    adminBillingRoute,
    adminF1SettingsRoute,
  ]),
  forbiddenRoute,
  fingerprintingRouteTree,
  immigrationRouteTree,
  notFoundRoute,
])
